import { api, ErrorApi } from '../api';
import type { Comprobante, EmitirRequest } from '../api';

/**
 * `POST /api/v1/comprobantes` (KAN-145). **El único camino de escritura**: lo
 * mismo que llaman los sistemas conectados, sin variante "para la UI" — ver el
 * javadoc de `ComprobantesController`.
 *
 * # POR QUÉ NO USA `desenvolver`
 *
 * `desenvolver` (`../api/cliente.ts`) trata cualquier respuesta que no sea
 * `2xx` como un {@link ErrorApi}, y para eso espera el cuerpo `{error,
 * detalle}` de `ManejadorDeErrores`. Acá **no alcanza**: el `422` de este
 * endpoint tiene DOS cuerpos posibles bajo el mismo código —
 *
 * - ARCA rechazó el comprobante → cuerpo `Comprobante` con
 *   `resultado: RECHAZADO`. **No es un error del servicio**: el servicio
 *   funcionó y ARCA dijo que no. Se corrige el pedido y se reintenta; no se
 *   lanza excepción para esto, se devuelve como un resultado más.
 * - Clave de idempotencia reusada con otro cuerpo → cuerpo `{error, detalle}`
 *   con `error: clave_idempotencia_reusada`. Este sí es un error.
 *
 * Que sean dos formas distintas bajo el mismo `422` es la razón de
 * {@link esResultadoDeArca}: se decide mirando la FORMA del cuerpo (si tiene
 * `resultado`), no leyendo el código HTTP.
 */
export async function emitirComprobante(
  pedido: EmitirRequest,
  claveDeIdempotencia: string,
): Promise<Comprobante> {
  let resultado;
  try {
    resultado = await api.POST('/api/v1/comprobantes', {
      params: { header: { 'Idempotency-Key': claveDeIdempotencia } },
      body: pedido,
    });
  } catch (causa) {
    // Sin respuesta: no se sabe si el POST llegó a reservar la clave. Quien
    // llama NO tiene que pedir una clave nueva para el reintento — ver
    // `idempotencia.ts`.
    throw ErrorApi.deRed(causa);
  }

  if (resultado.response.ok) {
    // 200 (reintento con clave ya completada) o 201 (recién emitido). Las dos
    // formas son el mismo `Comprobante`, siempre con `resultado: APROBADO`.
    return resultado.data as Comprobante;
  }
  if (resultado.response.status === 422 && esResultadoDeArca(resultado.error)) {
    // ARCA rechazó: se devuelve como dato, no se lanza. Quien llama decide
    // qué mostrar mirando `comprobante.resultado`.
    return resultado.error;
  }
  throw ErrorApi.deRespuesta(resultado.response.status, resultado.error);
}

/**
 * Distingue, dentro de un `422`, el rechazo de ARCA (forma `Comprobante`) del
 * error de clave reusada (forma `{error, detalle}`). Por la FORMA del cuerpo,
 * no por el código: los dos comparten el `422`.
 */
function esResultadoDeArca(cuerpo: unknown): cuerpo is Comprobante {
  return typeof cuerpo === 'object' && cuerpo !== null && 'resultado' in cuerpo;
}
